import React, { useEffect, useMemo, useState } from 'react';
import ExportButton from '../components/ExportButton';
import { fetchExpenses, createExpense, deleteExpenseById, fetchCategories } from '../services/backend-expenses';
import { getUser } from '../services/auth';
import api from '../api';
import './Expenses.css';

const pageSize = 10;

const inr2 = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function isSpendRow(e) {
  const t = e.transaction_type == null ? 'debit' : e.transaction_type;
  return t === 'debit';
}

function isIncomeRow(e) {
  return e.transaction_type === 'credit';
}

function monthBoundsIST() {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
  const today = fmt.format(new Date()); // YYYY-MM-DD
  const start = `${today.slice(0, 7)}-01`;
  return { start, end: today };
}

const emptyForm = (bank = '') => ({
  date: '',
  merchant: '',
  categoryId: '',
  amount: '',
  bank,
  transactionType: 'debit',
  balanceAfter: ''
});

const Expenses = () => {
  const user = getUser();
  const telegramId = user?.telegram_id;

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [category, setCategory] = useState('All');
  const [typeFilter, setTypeFilter] = useState('All');
  const [bankFilter, setBankFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [quick, setQuick] = useState('all'); // all | debit | credit | month
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);
  const [serverData, setServerData] = useState([]);
  const [serverCategories, setServerCategories] = useState([]);
  const [serverBanks, setServerBanks] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = async () => {
    const [list, cats, banks] = await Promise.all([
      fetchExpenses(telegramId),
      fetchCategories(telegramId),
      api.get('/api/categories/user-banks', { params: { telegramId } })
    ]);
    setServerData(list || []);
    setServerCategories(cats || []);
    const bankNames = (banks.data.banks || []).map((b) => b.bank_name);
    setServerBanks(bankNames);
    setForm((f) => ({ ...f, bank: f.bank || bankNames[0] || '' }));
  };

  useEffect(() => {
    if (telegramId) load();
  }, [telegramId]);

  // Clear selection when filters change (not on page change)
  useEffect(() => {
    setSelected([]);
  }, [startDate, endDate, category, typeFilter, bankFilter, query, quick]);

  const filtered = useMemo(() => {
    let list = serverData;
    if (startDate) list = list.filter((e) => e.transaction_date >= startDate);
    if (endDate) list = list.filter((e) => e.transaction_date <= endDate);
    if (category && category !== 'All') list = list.filter((e) => e.category_name === category);
    if (bankFilter && bankFilter !== 'All') {
      list = list.filter(
        (e) => String(e.bank || '').toLowerCase() === String(bankFilter).toLowerCase()
      );
    }
    if (typeFilter === 'Mandates') list = list.filter((e) => e.transaction_type === 'mandate');
    else if (typeFilter === 'Debit') list = list.filter((e) => isSpendRow(e));
    else if (typeFilter === 'Credit') list = list.filter((e) => isIncomeRow(e));
    else if (typeFilter === 'Exclude mandates') {
      list = list.filter((e) => e.transaction_type !== 'mandate');
    }
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((e) => (e.merchant || '').toLowerCase().includes(q));
    }
    return list;
  }, [serverData, startDate, endDate, category, typeFilter, bankFilter, query]);

  const rows = useMemo(
    () =>
      filtered.map((e) => ({
        id: e.id,
        date: e.transaction_date,
        merchant: e.merchant,
        category: e.category_name || 'Uncategorized',
        amount: Number(e.amount),
        bank: e.bank || '—',
        type: e.transaction_type || 'debit'
      })),
    [filtered]
  );

  const filteredSpend = useMemo(
    () => filtered.filter(isSpendRow).reduce((s, e) => s + Number(e.amount || 0), 0),
    [filtered]
  );
  const filteredIncome = useMemo(
    () => filtered.filter(isIncomeRow).reduce((s, e) => s + Number(e.amount || 0), 0),
    [filtered]
  );

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const rangeStart = rows.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const rangeEnd = Math.min(safePage * pageSize, rows.length);

  const exportRows = rows.map((e) => ({
    id: e.id,
    date: e.date,
    merchant: e.merchant,
    category: e.category,
    type: e.type,
    amount: e.amount,
    bank: e.bank
  }));

  const applyQuick = (key) => {
    setQuick(key);
    setPage(1);
    if (key === 'all') {
      setTypeFilter('All');
      setStartDate('');
      setEndDate('');
    } else if (key === 'debit') {
      setTypeFilter('Debit');
    } else if (key === 'credit') {
      setTypeFilter('Credit');
    } else if (key === 'month') {
      const { start, end } = monthBoundsIST();
      setStartDate(start);
      setEndDate(end);
    }
  };

  const activePills = useMemo(() => {
    const pills = [];
    if (typeFilter !== 'All') pills.push({ key: 'type', label: `Type: ${typeFilter}` });
    if (bankFilter !== 'All') pills.push({ key: 'bank', label: `Bank: ${bankFilter}` });
    if (category !== 'All') pills.push({ key: 'category', label: `Category: ${category}` });
    if (startDate) pills.push({ key: 'start', label: `From: ${startDate}` });
    if (endDate) pills.push({ key: 'end', label: `To: ${endDate}` });
    if (query) pills.push({ key: 'query', label: `Search: ${query}` });
    return pills;
  }, [typeFilter, bankFilter, category, startDate, endDate, query]);

  const clearPill = (key) => {
    setPage(1);
    if (key === 'type') {
      setTypeFilter('All');
      if (quick === 'debit' || quick === 'credit') setQuick('all');
    }
    if (key === 'bank') setBankFilter('All');
    if (key === 'category') setCategory('All');
    if (key === 'start') {
      setStartDate('');
      if (quick === 'month') setQuick('all');
    }
    if (key === 'end') {
      setEndDate('');
      if (quick === 'month') setQuick('all');
    }
    if (key === 'query') setQuery('');
  };

  const clearAllFilters = () => {
    setTypeFilter('All');
    setBankFilter('All');
    setCategory('All');
    setStartDate('');
    setEndDate('');
    setQuery('');
    setQuick('all');
    setPage(1);
  };

  const openDrawer = () => {
    setForm(emptyForm(serverBanks[0] || ''));
    setDrawerOpen(true);
  };

  const closeDrawer = () => setDrawerOpen(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount);
    if (!form.date || !form.merchant || !amt || !form.bank) {
      window.alert('Please fill date, merchant, amount, and bank.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        categoryId: form.categoryId || null,
        amount: amt,
        merchant: form.merchant,
        date: form.date,
        bank: form.bank,
        transactionType: form.transactionType || null
      };
      if (form.balanceAfter !== '') payload.balanceAfter = Number(form.balanceAfter);
      await createExpense(telegramId, payload);
      setDrawerOpen(false);
      setForm(emptyForm(serverBanks[0] || ''));
      await load();
      setPage(1);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm('Delete this transaction?')) return;
    await deleteExpenseById(id);
    setSelected((sel) => sel.filter((x) => x !== id));
    await load();
  };

  const onBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(`Delete ${selected.length} transaction${selected.length === 1 ? '' : 's'}?`);
    if (!ok) return;
    for (const id of selected) {
      await deleteExpenseById(id);
    }
    setSelected([]);
    await load();
  };

  const toggleSelect = (id) =>
    setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));

  const pageIds = paged.map((r) => r.id);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.includes(id));

  const toggleSelectPage = () => {
    if (allPageSelected) {
      setSelected((sel) => sel.filter((id) => !pageIds.includes(id)));
    } else {
      setSelected((sel) => [...new Set([...sel, ...pageIds])]);
    }
  };

  const pageButtons = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, safePage, safePage - 1, safePage + 1].filter((p) => p >= 1 && p <= totalPages));
    return [...set].sort((a, b) => a - b);
  }, [totalPages, safePage]);

  return (
    <div className="expenses-page">
      <p className="ex-eyebrow">PocketGuru · expenses</p>
      <h1 className="ex-title">Expenses</h1>
      <p className="ex-lead">
        Browse, filter, and correct transactions. Add opens a side drawer so the list stays primary.
      </p>

      <div className="ex-toolbar">
        <button type="button" className={`ex-chip ${quick === 'all' ? 'on' : ''}`} onClick={() => applyQuick('all')}>
          All
        </button>
        <button type="button" className={`ex-chip ${quick === 'debit' ? 'on' : ''}`} onClick={() => applyQuick('debit')}>
          Debits
        </button>
        <button type="button" className={`ex-chip ${quick === 'credit' ? 'on' : ''}`} onClick={() => applyQuick('credit')}>
          Credits
        </button>
        <button type="button" className={`ex-chip ${quick === 'month' ? 'on' : ''}`} onClick={() => applyQuick('month')}>
          This month
        </button>
        <input
          className="ex-field"
          type="date"
          title="Start"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
            if (quick === 'month') setQuick('all');
          }}
        />
        <input
          className="ex-field"
          type="date"
          title="End"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
            if (quick === 'month') setQuick('all');
          }}
        />
        <select
          className="ex-field"
          value={bankFilter}
          onChange={(e) => {
            setBankFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="All">Bank: All</option>
          {serverBanks.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
        </select>
        <select
          className="ex-field"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            setPage(1);
          }}
        >
          <option value="All">Category: All</option>
          {serverCategories.map((c) => (
            <option key={c.id} value={c.name}>
              {c.emoji ? `${c.emoji} ` : ''}
              {c.name}
            </option>
          ))}
        </select>
        <select
          className="ex-field"
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPage(1);
            const v = e.target.value;
            if (v === 'Debit') setQuick('debit');
            else if (v === 'Credit') setQuick('credit');
            else if (v === 'All') setQuick((q) => (q === 'debit' || q === 'credit' ? 'all' : q));
            else setQuick('all');
          }}
        >
          <option value="All">Type: All</option>
          <option value="Debit">Debit</option>
          <option value="Credit">Credit</option>
          <option value="Mandates">Mandates</option>
          <option value="Exclude mandates">Exclude mandates</option>
        </select>
        <input
          className="ex-field grow"
          type="search"
          placeholder="Search merchant…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
        />
        <span className="ex-spacer" />
        <ExportButton data={exportRows} />
        <button type="button" className="ex-btn primary" onClick={openDrawer}>
          + Add transaction
        </button>
      </div>

      {activePills.length > 0 && (
        <div className="ex-pills">
          {activePills.map((p) => (
            <span key={p.key} className="ex-pill">
              {p.label}
              <button type="button" aria-label={`Clear ${p.key}`} onClick={() => clearPill(p.key)}>
                ×
              </button>
            </span>
          ))}
          <button type="button" className="ex-pill-clear" onClick={clearAllFilters}>
            Clear all
          </button>
        </div>
      )}

      <div className="ex-stats">
        <div className="ex-stat">
          <div className="label">Filtered spend</div>
          <div className="value ex-debit">{inr2(filteredSpend)}</div>
        </div>
        <div className="ex-stat">
          <div className="label">Filtered income</div>
          <div className="value ex-credit">{inr2(filteredIncome)}</div>
        </div>
        <div className="ex-stat">
          <div className="label">Rows</div>
          <div className="value">
            {rows.length}{' '}
            <span style={{ fontSize: 13, color: 'var(--ex-muted)', fontWeight: 400 }}>
              · page {safePage} of {totalPages}
            </span>
          </div>
        </div>
      </div>

      {selected.length > 0 && (
        <div className="ex-selection">
          <span className="count">{selected.length} selected</span>
          <span className="hint">Only this page · clears when filters change</span>
          <span className="ex-spacer" />
          <button type="button" className="ex-btn ghost" onClick={() => setSelected([])}>
            Clear
          </button>
          <button type="button" className="ex-btn danger" onClick={onBulkDelete}>
            Delete selected…
          </button>
        </div>
      )}

      <div className="ex-card">
        <div className="ex-table-wrap">
          <table className="ex-table">
            <thead>
              <tr>
                <th style={{ width: 40 }}>
                  <input
                    type="checkbox"
                    title="Select page"
                    checked={allPageSelected}
                    onChange={toggleSelectPage}
                    disabled={paged.length === 0}
                  />
                </th>
                <th>When</th>
                <th>Merchant</th>
                <th>Category</th>
                <th>Bank</th>
                <th>Type</th>
                <th style={{ textAlign: 'right' }}>Amount</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <div className="ex-empty">No transactions match these filters.</div>
                  </td>
                </tr>
              )}
              {paged.map((e) => {
                const isCredit = e.type === 'credit';
                const isDebit = e.type === 'debit' || !e.type || e.type === '—';
                const typeClass = e.type === 'mandate' ? 'mandate' : isCredit ? 'credit' : 'debit';
                return (
                  <tr key={e.id} className={selected.includes(e.id) ? 'selected' : ''}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.includes(e.id)}
                        onChange={() => toggleSelect(e.id)}
                      />
                    </td>
                    <td>{e.date}</td>
                    <td>
                      <div className="ex-merchant">{e.merchant}</div>
                    </td>
                    <td>{e.category}</td>
                    <td>{e.bank}</td>
                    <td>
                      <span className={`ex-type-pill ${typeClass}`}>{e.type}</span>
                    </td>
                    <td
                      style={{ textAlign: 'right' }}
                      className={isCredit ? 'ex-credit' : isDebit ? 'ex-debit' : undefined}
                    >
                      {isCredit ? '+' : isDebit ? '−' : ''}
                      {inr2(e.amount)}
                    </td>
                    <td>
                      <div className="ex-row-actions">
                        <button type="button" className="ex-icon-btn danger" onClick={() => onDelete(e.id)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="ex-pager">
          <span>
            Showing {rangeStart}–{rangeEnd} of {rows.length}
          </span>
          <div className="pages">
            <button type="button" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
              Prev
            </button>
            {pageButtons.map((p, i) => {
              const prev = pageButtons[i - 1];
              const gap = prev != null && p - prev > 1;
              return (
                <React.Fragment key={p}>
                  {gap && <span>…</span>}
                  <button
                    type="button"
                    className={p === safePage ? 'on' : ''}
                    onClick={() => setPage(p)}
                  >
                    {p}
                  </button>
                </React.Fragment>
              );
            })}
            <button
              type="button"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <>
          <div className="ex-backdrop" onClick={closeDrawer} role="presentation" />
          <aside className="ex-drawer" aria-label="Add transaction">
            <h2>Add transaction</h2>
            <p className="sub">Manual entry when SMS didn’t fire — or to fill a gap.</p>
            <form className="ex-form-grid" onSubmit={onSubmit}>
              <div>
                <label>Type</label>
                <div className="ex-type-toggle">
                  <button
                    type="button"
                    className={`debit ${form.transactionType === 'debit' ? 'on' : ''}`}
                    onClick={() => setForm({ ...form, transactionType: 'debit' })}
                  >
                    Debit (spend)
                  </button>
                  <button
                    type="button"
                    className={`credit ${form.transactionType === 'credit' ? 'on' : ''}`}
                    onClick={() => setForm({ ...form, transactionType: 'credit' })}
                  >
                    Credit (income)
                  </button>
                </div>
              </div>
              <div className="ex-form-row">
                <div>
                  <label>Date</label>
                  <input
                    className="ex-field"
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label>Amount (₹)</label>
                  <input
                    className="ex-field"
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label>Merchant / description</label>
                <input
                  className="ex-field"
                  type="text"
                  placeholder="e.g. Swiggy, FLOW, Rent"
                  value={form.merchant}
                  onChange={(e) => setForm({ ...form, merchant: e.target.value })}
                  required
                />
              </div>
              <div className="ex-form-row">
                <div>
                  <label>Category</label>
                  <select
                    className="ex-field"
                    value={form.categoryId}
                    onChange={(e) => setForm({ ...form, categoryId: e.target.value })}
                  >
                    <option value="">(None)</option>
                    {serverCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.emoji ? `${c.emoji} ` : ''}
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label>Bank</label>
                  <select
                    className="ex-field"
                    value={form.bank}
                    onChange={(e) => setForm({ ...form, bank: e.target.value })}
                    required
                  >
                    {serverBanks.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label>Balance after (optional)</label>
                <input
                  className="ex-field"
                  type="number"
                  step="0.01"
                  placeholder="Only if you know Avl Bal"
                  value={form.balanceAfter}
                  onChange={(e) => setForm({ ...form, balanceAfter: e.target.value })}
                />
              </div>
              {serverBanks.length === 0 && (
                <p className="ex-note">No banks set. Add a bank in Settings → Banks first.</p>
              )}
              <div className="ex-drawer-actions">
                <button type="button" className="ex-btn ghost" onClick={closeDrawer}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="ex-btn primary"
                  disabled={saving || serverBanks.length === 0}
                >
                  {saving ? 'Saving…' : 'Save transaction'}
                </button>
              </div>
            </form>
          </aside>
        </>
      )}
    </div>
  );
};

export default Expenses;
